import { inject, Injectable, NgZone } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { ReplaySubject, Subject } from 'rxjs'

import { TableLazyLoadEvent } from 'primeng/table'

import { StaticDropDown } from '@app/components/generic/generic'
import { GenericColumnComponent } from '@app/components/generic/generic-column'
import { asArray, assertUnreachable, buildFilterKeyValue, Params } from '@app/components/generic/utilities'

import { FilterServicePayload, newFilterServicePayload } from './filter'
import { FilterType } from './filter.constants'

@Injectable({ providedIn: 'root' })
export class ConfigureFiltersService {
  private readonly router = inject(Router)
  private readonly activatedRoute = inject(ActivatedRoute)
  private readonly ngZone = inject(NgZone)

  // map of uniqueId -> list of GenericColumnComponents
  private columnsMap: Map<string, GenericColumnComponent[]> = new Map<string, GenericColumnComponent[]>()
  private columnsMapSubject = new ReplaySubject<Map<string, GenericColumnComponent[]>>()

  // map of uniqueId -> list of FilterPayloads
  private filtersPayloadMap: Map<string, FilterServicePayload> = new Map<string, FilterServicePayload>()
  private filtersPayloadSubject = new ReplaySubject<Map<string, FilterServicePayload>>()

  // subject for triggering that the filters have changed, i.e. to call the table to refresh
  private filtersChangedSubject = new Subject<{ uniqueKey: string, payload: FilterServicePayload }>()

  // In order to add the 'Global search' option to the filters widget we need to 'fake' a column on the table
  public globalColumnComponent: GenericColumnComponent = {
    title: $localize`:@@configure-filters-global-search:Global search`,
    key: 'global_search',
    filterType: FilterType.GLOBAL,
    filterOptions: [],
    filterLabelField: '',
    enableSort: false,
    group: false,
    showMatchModes: false,
    matchMode: 'equals',
    checkedFilterGroups: [],
    isMobileColumn: false,
  }

  public getFilters () {
    return this.filtersPayloadSubject.asObservable()
  }

  public setFilters (uniqueKey: string, payload: FilterServicePayload) {
    this.filtersPayloadMap.set(uniqueKey, payload)
    this.filtersPayloadSubject.next(this.filtersPayloadMap)
  }

  // call this from filter widget to trigger filter refresh in table
  public setFiltersChanged (message: { uniqueKey: string, payload: FilterServicePayload }) {
    this.setFilters(message.uniqueKey, message.payload)
    this.filtersChangedSubject.next(message)
  }

  public clearFilters (tableUniqueKey: string) {
    this.setFiltersChanged({ uniqueKey: tableUniqueKey, payload: newFilterServicePayload() })
  }

  // listen to this from the client table to receive new filters
  public getFiltersChangedObservable () {
    return this.filtersChangedSubject.asObservable()
  }

  public setColumns (uniqueKey: string, columnComponents: GenericColumnComponent[]) {
    this.columnsMap.set(uniqueKey, columnComponents)
    this.columnsMapSubject.next(this.columnsMap)
    this.attachColumns(uniqueKey)
    // normally the setting of the columns signals a client (table) is ready for use,
    // so we call for the filters to be updated now
    if (this.filtersPayloadMap.get(uniqueKey)) {
      this.setFiltersChanged({ uniqueKey: uniqueKey, payload: this.filtersPayloadMap.get(uniqueKey)! })
    }
  }

  // listen to this from the client table to receive the list of all the columns that are filterable
  public getColumns () {
    return this.columnsMapSubject.asObservable()
  }

  public lazyLoadEventToFilterServicePayload (lazyLoadEvent: TableLazyLoadEvent, uniqueKey: string): FilterServicePayload {
    this.filtersPayloadMap.delete(uniqueKey)
    const payload = newFilterServicePayload()

    for (const key in lazyLoadEvent.filters ?? {}) {
      if (lazyLoadEvent.filters![key]) {
        const filterMetaDatas = asArray(lazyLoadEvent.filters![key])

        filterMetaDatas?.forEach(filter => {
          if (filter.value === null) return

          const newFilter = {
            value: filter.value,
            matchMode: filter.matchMode,
            operator: filter.operator,

            filterKey: key,
            filterMatchModes: [],
          }

          if (filter.matchMode === 'exists' && filter.value === false) {
            newFilter.matchMode = 'notExists'
            newFilter.value = true
          }
          payload.filters.push(newFilter)
        })
      }
    }
    payload.first = lazyLoadEvent.first ?? payload.first
    payload.rows = lazyLoadEvent.rows ?? payload.rows
    payload.multiSortMeta = structuredClone(lazyLoadEvent.multiSortMeta ?? [])
    this.setFilters(uniqueKey, payload)
    this.attachColumns(uniqueKey)
    return payload
  }

  public resetURL (uniqueKey: string) {
    const urlParams: Params = {}

    const filtersPayload = this.filtersPayloadMap.get(uniqueKey) || newFilterServicePayload()
    filtersPayload.filters.forEach(filter => {
      if (filter.value === null) {
        return
      }
      const [filterKey, filterValue] = buildFilterKeyValue(filter, filter.filterKey!)
      // pre-pend the url params with the table unique key, in order to allow for multi-layer filters in the url :O
      urlParams[`${uniqueKey}___${filterKey}`] = filterValue
    })

    urlParams[`${uniqueKey}___limit`] = filtersPayload.rows || 100
    urlParams[`${uniqueKey}___offset`] = filtersPayload.first || 0

    // build multi-field sort
    const orderFields: string[] = []
    filtersPayload.multiSortMeta?.forEach(sortData => {
      let sortField = sortData.field
      if (sortData.order === -1) {
        sortField = '-' + sortField
      }
      orderFields.push(sortField)
    })
    urlParams[`${uniqueKey}___ordering`] = orderFields.join(',')

    let replaceUrl = false
    // If there are no url params after a generic list url, then we replace history instead of appending.
    // This is to avoid the 'double back' obligation for the case where a list page makes an immediate redirect for
    // its default sort / filter, we don't want to store the initial /list url in the history.
    if (this._isRootListURL(this.router.url)) {
      replaceUrl = true
    }

    this.ngZone.run(() => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: urlParams,
        replaceUrl: replaceUrl,
      })
    })
  }

  /**
   * Determine if the given URL is a 'root' list url, i.e. http://localhost:4200/projects/list ,
   *  as opposed to a list with standard params already added: http://localhost:4200/projects/list?offset=0&limit=100&ordering=date ,
   *  or a detail page which itself includes a list.
   *
   * Why do we not use :
   *     if (theUrl.endsWith('list')) return true
   * ?
   *
   * The unsatisfactory answer is that a detail page may include a list which updates its url, e.g.:
   *  detail/13?offset=0&limit=100&ordering=id
   * In the above example we want to treat the 'root' as detail/13.
   *
   * In reality all detail pages should disallow their lists from touching the url probably, at which time we can use the above instead.
   */
  private _isRootListURL (theUrl: string) : boolean {
    return (theUrl.indexOf('offset=') === -1) && (theUrl.indexOf('limit=') === -1)
  }

  public attachColumns (uniqueKey: string) {
    const columnsMap = this.columnsMap.get(uniqueKey)
    if (!columnsMap) return

    const filtersPayloadMap = this.filtersPayloadMap.get(uniqueKey)
    if (!filtersPayloadMap) return

    filtersPayloadMap.filters.forEach(filter => {
      if (filter.filterKey === 'global_search') {
        filter.columnComponent = this.globalColumnComponent
      } else {
        filter.columnComponent = columnsMap.find(column => filter.filterKey === (column.filterKey ?? column.key))
        // If the filter key does not match any column, remove the filter.
        if (filter.columnComponent === undefined) filtersPayloadMap.filters.splice(filtersPayloadMap.filters.indexOf(filter), 1)
      }
      filter.columnKey = filter.columnComponent?.key
      filter.filterMatchModes = this.getFilterMatchModes(filter.columnComponent?.filterType)
    })
    this.setFilters(uniqueKey, filtersPayloadMap)
  }

  public getFilterMatchModes (filterType?: FilterType): StaticDropDown[] {
    switch (filterType) {
      case FilterType.GLOBAL: // special case for the global search 'fake field' in the list of fields
      case FilterType.SELECT: return [{
        value: 'equals',
        label_en: 'Equals',
        label_fr: 'Est égal à',
      }]

      case FilterType.MSELECT: return [{
        value: 'in',
        label_en: 'Includes',
        label_fr: 'Inclue',
      }]

      case FilterType.DATE:
      case FilterType.MONTH: return [{
        value: 'dateIs',
        label_en: 'Exact',
        label_fr: 'Est',
      }, {
        value: 'dateBefore',
        label_en: 'Before',
        label_fr: 'Est avant',
      }, {
        value: 'dateAfter',
        label_en: 'After',
        label_fr: 'Est après',
      }, {
        value: 'exists',
        label_en: 'Exists',
        label_fr: 'Existe',
      }, {
        value: 'notExists',
        label_en: 'Not exists',
        label_fr: 'N’existe pas',
      }]

      case FilterType.NUMERIC: return [{
        value: 'equals',
        label_en: 'Equals',
        label_fr: 'Égal',
      }, {
        value: 'notEquals',
        label_en: 'Not equals',
        label_fr: 'Différent de',
      }, {
        value: 'lt',
        label_en: 'Less than',
        label_fr: 'Inférieur à',
      }, {
        value: 'lte',
        label_en: 'Less than or equal',
        label_fr: 'Inférieur ou égal à',
      }, {
        value: 'gt',
        label_en: 'Greater than',
        label_fr: 'Supérieur à',
      }, {
        value: 'gte',
        label_en: 'Greater than or equal',
        label_fr: 'Supérieur ou égal à',
      }, {
        value: 'exists',
        label_en: 'Exists',
        label_fr: 'Existe',
      }, {
        value: 'notExists',
        label_en: 'Not exists',
        label_fr: 'N’existe pas',
      }]

      case FilterType.TEXT:
      case FilterType.NONE:
      case undefined: return [{
        value: 'contains',
        label_en: 'Contains',
        label_fr: 'Contient',
      }, {
        value: 'notContains',
        label_en: 'Not contains',
        label_fr: 'Ne contient pas',
      }, {
        value: 'equals',
        label_en: 'Equals',
        label_fr: 'Est égal à',
      }, {
        value: 'notEquals',
        label_en: 'Not equals',
        label_fr: 'Est différent de',
      }, {
        value: 'startsWith',
        label_en: 'Starts with',
        label_fr: 'Commence par',
      }, {
        value: 'endsWith',
        label_en: 'Ends with',
        label_fr: 'Fini par',
      }, {
        value: 'exists',
        label_en: 'Exists',
        label_fr: 'Existe',
      }, {
        value: 'notExists',
        label_en: 'Not exists',
        label_fr: 'N’existe pas',
      }]

      default:
        return assertUnreachable(filterType)
    }
  }
}
